import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Typography,
  Table,
  Sheet,
  CircularProgress,
  Select,
  Option,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { supabase } from "../../supabaseClient";
import { Class, ClassStatus, calculateClassStatus } from "../../types/Class";
import { Teacher } from "../../types/Teacher";
import { fetchProfilePicture, getInitials } from "../../utils";
import { formatScheduleDisplay } from "../../utils/schedule-formatting";
import { StatusBadge } from "../../utils/classes";
import { useModal } from "../../context/ModalContext";
import { usePagination } from "../../hooks/usePagination";
import Pagination from "../Pagination";
import AddEditClassModal from "../modals/AddEditClassModal";
import EnrollStudentModal from "../modals/EnrollStudentModal";
import { useToast } from "../../context/ToastContext";

interface ClassesTableProps {
  studentId?: string;
  initialStatus?: ClassStatus;
  isStudentDetailView?: boolean;
}



const ClassesTable: React.FC<ClassesTableProps> = ({ studentId, initialStatus, isStudentDetailView }) => {
  const [classes, setClasses] = useState<Class[]>([]);
  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<ClassStatus | "all">(initialStatus || "all");
  const [selectedPrimaryTeacher, setSelectedPrimaryTeacher] = useState<string | "all">("all");
  const { showToast } = useToast();
  const { openModal } = useModal();
  const navigate = useNavigate();

  // Sort and filter classes
  const processedClasses = React.useMemo(() => {
    const sortedClasses = [...classes].sort((classA, classB) => {
      const statusOrder = {
        [ClassStatus.Current]: 0,
        [ClassStatus.Future]: 1,
        [ClassStatus.Past]: 2
      };
      const statusA = calculateClassStatus(classA);
      const statusB = calculateClassStatus(classB);
      return statusOrder[statusA] - statusOrder[statusB];
    });

    // Filter by status
    let filteredClasses = sortedClasses;
    if (selectedStatus !== "all") {
      filteredClasses = filteredClasses.filter(cls => {
        const status = calculateClassStatus(cls);
        return status === selectedStatus;
      });
    }

    // Filter by primary teacher
    if (selectedPrimaryTeacher !== "all") {
      filteredClasses = filteredClasses.filter(cls => {
        return cls.primary_teacher_id === selectedPrimaryTeacher;
      });
    }

    return filteredClasses;
  }, [classes, selectedStatus, selectedPrimaryTeacher]);

  // Use pagination hook
  const {
    paginatedData: paginatedClasses,
    currentPage,
    totalPages,
    totalItems,
    rowsPerPage,
    setCurrentPage,
    setRowsPerPage,
  } = usePagination({
    data: processedClasses,
    searchTerm: "", // Classes table doesn't have search, only status filtering
    searchFields: [],
    filters: {}, // Status filtering is handled in processedClasses
  });

  // Fetch a single class with relationships and profile pictures
  const fetchClassWithRelations = async (classId: string) => {
    try {
      const joinClause = studentId ? 'classes_students!inner(student_id, enrollment_type)' : 'classes_students!left(student_id, enrollment_type)';
      let query = supabase
        .from("classes_view")
        .select(`
          *,
          primary_teacher:primary_teacher_id ( * ),
          secondary_teacher:secondary_teacher_id ( * ),
          ${joinClause},
          student_count:classes_students(count)
        `) as any;

      // Add filters after type assertion
      query = query.eq("id", classId).single();

      // Conditionally add the student filter if it's provided
      if (studentId) {
        query = query.eq("classes_students.student_id", studentId);
      }

      const { data, error } = await query;

      if (error || !data) {
        throw error;
      }

      const primaryTeacher = data.primary_teacher || null;
      const secondaryTeacher = data.secondary_teacher || null;

      // Fetch profile pictures for both primary and secondary teachers
      if (primaryTeacher) {
        primaryTeacher.profile_picture = await fetchProfilePicture("teachers", primaryTeacher.profile_picture_id);
      }
      if (secondaryTeacher) {
        secondaryTeacher.profile_picture = await fetchProfilePicture("teachers", secondaryTeacher.profile_picture_id);
      }

      return {
        ...data,
        primary_teacher: primaryTeacher,
        secondary_teacher: secondaryTeacher,
        classes_students: data.all_students || []
      } as Class;
    } catch (error) {
      console.error("Error fetching class with relations:", error);
      return null;
    }
  };

  // Fetch teachers from Supabase
  const fetchTeachers = async () => {
    try {
      const { data, error } = await supabase
        .from("teachers")
        .select("*")
        .order("first_name");

      if (error) {
        throw error;
      }

      // Format teachers by fetching profile pictures
      const formattedTeachers = await Promise.all(
        data.map(async (teacher: any) => {
          const profilePicture = await fetchProfilePicture("teachers", teacher.profile_picture_id);
          return {
            ...teacher,
            profile_picture: profilePicture
          };
        })
      );

      setTeachers(formattedTeachers);
    } catch (error) {
      console.error("Error fetching teachers:", error);
      showToast("Error fetching teachers.", "danger", "solid");
    }
  };

  // Fetch classes from Supabase
  const fetchClasses = async () => {
    setLoading(true);
    try {
      // Initialize the query from the classes_view table
      const joinClause = studentId ? 'classes_students!inner(student_id, enrollment_type)' : 'classes_students!left(student_id, enrollment_type)';
      let query = supabase
        .from("classes_view")
        .select(`
          *,
          primary_teacher:primary_teacher_id ( * ),
          secondary_teacher:secondary_teacher_id ( * ),
          ${joinClause},
          student_count:classes_students(count)
        `) as any;

      // Apply the student filter if provided
      if (studentId) {
        query = query.eq("classes_students.student_id", studentId);
      }

      // Fetch the data from Supabase
      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Format the classes by fetching profile pictures for teachers
      const formattedClasses = await Promise.all(
        data.map(async (cls: any) => {
          const primaryTeacher = cls.primary_teacher || null;
          const secondaryTeacher = cls.secondary_teacher || null;

          if (primaryTeacher) {
            primaryTeacher.profile_picture = await fetchProfilePicture("teachers", primaryTeacher.profile_picture_id);
          }
          if (secondaryTeacher) {
            secondaryTeacher.profile_picture = await fetchProfilePicture("teachers", secondaryTeacher.profile_picture_id);
          }

          // Get the total student count regardless of the current filter
          const { count } = await supabase
            .from("classes_students")
            .select("*", { count: "exact", head: true })
            .eq("class_id", cls.id);

          return {
            ...cls,
            primary_teacher: primaryTeacher,
            secondary_teacher: secondaryTeacher,
            student_count: { count: count || 0 }
          };
        })
      );

      setClasses(formattedClasses);
    } catch (error) {
      console.error("Error fetching classes:", error);
      showToast("Error fetching classes.", "danger", "solid");
    } finally {
      setLoading(false);
    }
  };

  const handleClassChange = async (payload: any) => {
    if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
      const updatedClass = await fetchClassWithRelations(payload.new.id);
      if (updatedClass) {
        setClasses((prevClasses) => {
          // Replace the existing class if it's an update, or add it if it's an insert
          const classIndex = prevClasses.findIndex((cls) => cls.id === updatedClass.id);
          if (classIndex !== -1) {
            const updatedClasses = [...prevClasses];
            updatedClasses[classIndex] = updatedClass;
            return updatedClasses;
          } else {
            return [...prevClasses, updatedClass];
          }
        });
      }
    } else if (payload.eventType === "DELETE") {
      setClasses((prevClasses) =>
        prevClasses.filter((cls) => cls.id !== payload.old.id)
      );
    }
  };

  const handleEnrollmentChange = async (eventObject: any) => {
    console.log("Event received:", eventObject); // Debug log
    if (eventObject.eventType === "INSERT") {
      console.log("Enrollment INSERT event received:", eventObject.new); // Debug log
      const newClassId = eventObject.new.class_id;
      const newClass = await fetchClassWithRelations(newClassId);
      if (newClass) {
        setClasses((prevClasses) => {
          // Only add if the class isn't already in the list
          if (!prevClasses.find((cls) => cls.id === newClass.id)) {
            return [...prevClasses, newClass];
          }
          return prevClasses;
        });
      }
    } else if (eventObject.eventType === "DELETE") {
      console.log("Enrollment DELETE event received:", eventObject.old); // Debug log
      const removedClassId = eventObject.old.class_id;
      setClasses((prevClasses) => {
        return prevClasses.filter((cls) => cls.id !== removedClassId);
      });
    }
  };

  useEffect(() => {
    fetchClasses();
    fetchTeachers();

    // Subscribe to changes in the "classes" table
    const subscription = supabase
      .channel("public:classes")
      .on(
        "postgres_changes", 
        { event: "*", schema: "public", table: "classes" }, 
        (payload) => {
          // console.log("Change received!", payload);
          handleClassChange(payload);
        }
      )
      .subscribe();

    // Subscribe to changes in the "classes_students" table
    const enrollmentSubscription = supabase
      .channel("public:classes_students")
      .on(
        "postgres_changes", 
        { event: "*", schema: "public", table: "classes_students" }, 
        (payload) => {
          // console.log("Enrollment change received!", payload);
          handleEnrollmentChange(payload);
        }
      )
      .subscribe();

    // Subscribe to changes in the "teachers" table
    const teacherSubscription = supabase
      .channel("public:teachers")
      .on(
        "postgres_changes", 
        { event: "*", schema: "public", table: "teachers" }, 
        (payload) => {
          // console.log("Teacher change received!", payload);
          fetchTeachers();
        }
      )
      .subscribe();

    // Cleanup the subscriptions on component unmount
    return () => {
      supabase.removeChannel(subscription);
      supabase.removeChannel(enrollmentSubscription);
      supabase.removeChannel(teacherSubscription);
    };
    /* eslint-disable-next-line */
  }, []);

  const handleEnrollStudent = () => {
    openModal(<EnrollStudentModal studentId={studentId} onEnrollmentUpdated={fetchClasses} />);
  };

  const handleRemoveEnrollment = async (classData: Class) => {
    if (!studentId) return;

    try {
      const { error } = await supabase
        .from("classes_students")
        .delete()
        .eq("class_id", classData.id)
        .eq("student_id", studentId);

      if (error) {
        throw error;
      }

      showToast("Student removed from class successfully.", "success", "solid");
    } catch (error) {
      console.error("Error removing student from class:", error);
      showToast("Error removing student from class.", "danger", "solid");
    }
  };

  const handleAddClass = () => {
    openModal(<AddEditClassModal />);
  };

  const handleRowClick = (classId: string) => {
    navigate(`/classes/${classId}`);
  };

  return (
    <React.Fragment>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Select<ClassStatus | "all">
            value={selectedStatus}
            onChange={(_, value) => value && setSelectedStatus(value)}
            sx={{ minWidth: 150 }}
          >
            <Option value="all">All Classes</Option>
            <Option value={ClassStatus.Current}>Current</Option>
            <Option value={ClassStatus.Future}>Future</Option>
            <Option value={ClassStatus.Past}>Past</Option>
          </Select>
          <Select<string | "all">
            value={selectedPrimaryTeacher}
            onChange={(_, value) => value && setSelectedPrimaryTeacher(value)}
            sx={{ minWidth: 200 }}
          >
            <Option value="all">All Primary Teachers</Option>
            {teachers.map((teacher) => (
              <Option key={teacher.id} value={teacher.id}>
                {teacher.first_name} {teacher.last_name}
              </Option>
            ))}
          </Select>
          {isStudentDetailView ? (
            <Button startDecorator={<AddIcon />} onClick={handleEnrollStudent}>
              Enroll Student in Class
            </Button>
          ) : (
            <Button startDecorator={<AddIcon />} onClick={handleAddClass}>
              Add Class
            </Button>
          )}
        </Box>
        <Box /> {/* Empty box to maintain space-between layout */}
      </Box>
      <Sheet variant="outlined" sx={{ width: "100%", borderRadius: "sm", overflow: "auto", minHeight: 0 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : classes.length > 0 ? (
          <>
            {paginatedClasses.length > 0 ? (
              <>
                <Table>
                  <thead>
                    <tr>
                      <th>Class Status</th>
                      <th>Term</th>
                      <th>Primary Teacher</th>
                      <th>Secondary Teacher</th>
                      <th>Schedule</th>
                      {isStudentDetailView && <th>Enrollment Type</th>}
                      <th># of Students</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClasses.map((classItem: Class) => {
                      const status = calculateClassStatus(classItem);
                      return (
                        <tr 
                          key={classItem.id} 
                          onClick={() => handleRowClick(classItem.id)}
                          style={{ cursor: 'pointer' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--joy-palette-neutral-100)'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = ''}
                        >
                          <td style={{ textAlign: "start", verticalAlign: "middle" }}>
                            <StatusBadge status={status} />
                          </td>
                          <td>{classItem.start_month}/{classItem.start_year} — {classItem.end_month}/{classItem.end_year}</td>
                          <td style={{ verticalAlign: "middle" }}>
                            {classItem.primary_teacher_id && classItem.primary_teacher ? (           
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  key={classItem.primary_teacher_id}
                                  src={classItem.primary_teacher?.profile_picture || undefined}
                                  sx={{ width: 32, height: 32 }}
                                >
                                  {!classItem.primary_teacher?.profile_picture ? getInitials(classItem.primary_teacher) : null}
                                </Avatar>
                                <Typography>{classItem.primary_teacher.first_name} {classItem.primary_teacher.last_name}</Typography>
                              </Box>
                            ) : (
                              <Typography>No Teacher Assigned</Typography>
                            )}
                          </td>
                          <td style={{ verticalAlign: "middle" }}>
                            {classItem.secondary_teacher_id && classItem.secondary_teacher ? (           
                              <Box display="flex" alignItems="center" gap={1}>
                                <Avatar
                                  key={classItem.secondary_teacher_id}
                                  src={classItem.secondary_teacher?.profile_picture || undefined}
                                  sx={{ width: 32, height: 32 }}
                                >
                                  {!classItem.secondary_teacher?.profile_picture ? getInitials(classItem.secondary_teacher) : null}
                                </Avatar>
                                <Typography>{classItem.secondary_teacher.first_name} {classItem.secondary_teacher.last_name}</Typography>
                              </Box>
                            ) : (
                              <Typography>No Teacher Assigned</Typography>
                            )}
                          </td>
                          <td>
                            <Typography level="body-sm">
                              {formatScheduleDisplay(classItem.schedule)}
                            </Typography>
                          </td>
                          {isStudentDetailView && (
                            <td>
                              <Typography>
                                {(classItem.classes_students?.[0]?.enrollment_type || "full") === "half" ? (
                                  <span style={{ color: "var(--joy-palette-primary-500)" }}>🕐 Half Day</span>
                                ) : (
                                  <span style={{ color: "var(--joy-palette-success-500)" }}>🕐 Full Day</span>
                                )}
                              </Typography>
                            </td>
                          )}
                          <td>
                            <Typography>
                              {(classItem.student_count as any)?.count || 0}
                            </Typography>
                          </td>
                          <td>
                            <Box display="flex" gap={1} alignItems="center">
                              {isStudentDetailView ? (
                                <Tooltip title="Remove Student from Class">
                                  <IconButton 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRemoveEnrollment(classItem);
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              ) : null}
                              <Tooltip title="View Class Details">
                                <IconButton 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRowClick(classItem.id);
                                  }}
                                >
                                  <ChevronRightIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  rowsPerPage={rowsPerPage}
                  onPageChange={setCurrentPage}
                  onRowsPerPageChange={setRowsPerPage}
                />
              </>
            ) : (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <Typography>No classes match the selected filter.</Typography>
              </Box>
            )}
          </>
        ) : (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>No classes found</Typography>
          </Box>
        )}
      </Sheet>
    </React.Fragment>
  );
};

export default ClassesTable;